import { useDispatch, useSelector } from 'react-redux';

import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../containers/Tooltip';
import { Button } from '../../../Button/Button';
import { ButtonGroup } from '../../../Button/ButtonGroup';
import {
  DECREASE_ATOM_CHARGE_MODE,
  DEFAULT_MODE,
  INCREASE_ATOM_CHARGE_MODE,
  MOVE_MODE,
  REMOVE_MODE,
  ROTATE_MODE,
  SELECT_MODE,
  TOGGLE_BOND_DASHED_MODE,
  TOGGLE_BOND_WEDGED_MODE,
} from '../../etc/constants';
import { setMode } from '../../utils/molecule-object/molecule-object.actions';

import { styled } from '../../../../../stitches.config';

const ButtonContainer = styled('div', {
  'display': 'flex',
  'flexDirection': 'column',
  'justifyContent': 'space-between',
  '&[hidden]': {
    opacity: 0,
    height: 0,
    pointerEvents: 'none',
  },
});

const buttonCss = {
  borderRadius: '$small',
  fontSize: '$4',
};

type ModeToolbarProps = {
  hidden: boolean;
};

export function ModeToolbar({ hidden }: ModeToolbarProps) {
  const dispatch = useDispatch();
  const selectedMode = useSelector((state: any) => state.moleculeObject.mode.type);

  const onClick = (mode: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    dispatch(setMode(mode));
  };

  const renderModeButton = (mode: string, icon: string | React.ReactNode, hint: string) => (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <Button
          as={TooltipTrigger}
          weight="minimal"
          active={selectedMode === mode}
          icon={icon as string}
          onClick={onClick(mode)}
          css={buttonCss}
        />
        <TooltipPortal>
          <TooltipContent side="right">
            {hint}
            <TooltipArrow />
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <ButtonContainer hidden={hidden}>
      <ButtonGroup direction="column">
        {renderModeButton(DEFAULT_MODE, 'edit', 'Add')}
        {renderModeButton(SELECT_MODE, 'tab_unselected', 'Select')}
        {renderModeButton(ROTATE_MODE, 'rotate_90_degrees_cw', 'Rotate')}
        {renderModeButton(MOVE_MODE, 'open_with', 'Move')}
        {renderModeButton(REMOVE_MODE, 'delete_forever', 'Remove')}
        {renderModeButton(INCREASE_ATOM_CHARGE_MODE, 'add', 'Increase charge')}
        {renderModeButton(DECREASE_ATOM_CHARGE_MODE, 'remove', 'Decrease charge')}
        {renderModeButton(
          TOGGLE_BOND_DASHED_MODE,
          // how to add pattern fill to svg: stackoverflow.com/questions/13069446/simple-fill-pattern-in-svg-diagonal-hatching
          // how to use custom svg icon in blueprint: github.com/palantir/blueprint/issues/2348
          // path in this svg was copied from path defined in 'symbol-triangle-down'
          <svg width="16" height="16" viewBox="0 0 16 16">
            <defs>
              <pattern id="diagonalHatch" width="3" height="3" patternUnits="userSpaceOnUse">
                <line x1="0" y1="0" x2="4" y2="0" style={{ stroke: '#5c7080', strokeWidth: 3 }} />
              </pattern>
            </defs>
            <desc>symbol-triangle-up</desc>
            <path
              d="M13 4.01c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 .16.05.31.11.44H3.1l4 8h.01c.16.33.49.56.89.56s.72-.23.89-.56h.01l4-8h-.01c.06-.14.11-.28.11-.44z"
              fillRule="evenodd"
              fill="url(#diagonalHatch)"
            />
          </svg>,
          'Toggle dashed bond'
        )}
        {renderModeButton(
          TOGGLE_BOND_WEDGED_MODE,
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path
              d="M13 4.01c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 .16.05.31.11.44H3.1l4 8h.01c.16.33.49.56.89.56s.72-.23.89-.56h.01l4-8h-.01c.06-.14.11-.28.11-.44z"
              fillRule="evenodd"
            ></path>
          </svg>,
          'Toggle wedged bond'
        )}
      </ButtonGroup>
    </ButtonContainer>
  );
}
